import React, { useState, useRef } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { Check, ChevronDown } from 'lucide-react';
import { Property } from '../../types/types';
import { clsx } from 'clsx';

const STATUS_OPTIONS = [
    { status: 'Disponible' as const, bg: 'bg-emerald-500', hover: 'hover:bg-emerald-400', ring: 'ring-emerald-300', badge: 'bg-emerald-500' },
    { status: 'Con Reserva' as const, bg: 'bg-amber-500', hover: 'hover:bg-amber-400', ring: 'ring-amber-300', badge: 'bg-amber-500' },
    { status: 'Vendido' as const, bg: 'bg-rose-500', hover: 'hover:bg-rose-400', ring: 'ring-rose-300', badge: 'bg-rose-500' },
    { status: 'Alquilado' as const, bg: 'bg-blue-500', hover: 'hover:bg-blue-400', ring: 'ring-blue-300', badge: 'bg-blue-500' },
    { status: 'Fuera de mercado' as const, bg: 'bg-slate-500', hover: 'hover:bg-slate-400', ring: 'ring-slate-300', badge: 'bg-slate-500' },
];

interface StatusPickerProps {
    propertyId: string;
    currentStatus: string /*Property['status']*/;
    onStatusChange?: (id: string, newStatus: string /*Property['status']*/) => void;
    /** Align the popover to the left (default) or right of the badge */
    align?: 'left' | 'right';
    /** 'compact' works well in cards, 'large' works well in headers */
    variant?: 'compact' | 'large';
}

export const StatusPicker: React.FC<StatusPickerProps> = ({
    propertyId,
    currentStatus,
    onStatusChange,
    align = 'left',
    variant = 'compact'
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Close on outside click
    useClickOutside(ref, () => setOpen(false), open);

    const current = STATUS_OPTIONS.find(o => o.status === currentStatus)!;

    return (
        <div ref={ref} className="relative inline-block" onClick={e => e.stopPropagation()}>
            {/* Badge trigger */}
            <button
                onClick={() => setOpen(prev => !prev)}
                className={clsx(
                    "flex items-center transition-all hover:brightness-110 active:scale-95 text-white",
                    variant === 'large'
                        ? "gap-2 px-4 py-2 rounded-full text-xs font-bold"
                        : "gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wide",
                    current.badge
                )}
            >
                {currentStatus}
                <ChevronDown
                    size={variant === 'large' ? 14 : 10}
                    strokeWidth={3}
                    className={clsx(
                        "transition-transform duration-200",
                        open ? "rotate-180" : ""
                    )}
                />
            </button>

            {/* Floating popover */}
            <div
                className={clsx(
                    "absolute top-[calc(100%+6px)] z-50 transition-all duration-200",
                    align === 'right' ? "right-0" : "left-0",
                    open
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-1 pointer-events-none"
                )}
            >
                <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10 backdrop-blur-xl bg-slate-900/95 min-w-[200px]">
                    <div className="px-3 pt-2.5 pb-1">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Cambiar estado</p>
                    </div>
                    <div className="flex flex-col gap-1 px-1.5 pb-1.5">
                        {STATUS_OPTIONS.map(({ status, bg, badge }) => {
                            const isActive = currentStatus === status;
                            return (
                                <button
                                    key={status}
                                    onClick={() => {
                                        onStatusChange?.(propertyId, status);
                                        setOpen(false);
                                    }}
                                    className={clsx(
                                        "flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-white transition-all duration-200 active:scale-95",
                                        isActive
                                            ? `${bg} shadow-md opacity-100`
                                            : "hover:bg-white/10 opacity-70 hover:opacity-100"
                                    )}
                                >
                                    <div className={clsx("w-2 h-2 rounded-full shrink-0 shadow-sm", isActive ? "bg-white" : badge)} />
                                    <span className="text-xs font-bold tracking-wide">{status}</span>
                                    {isActive && <Check size={14} strokeWidth={3} className="ml-auto opacity-90" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
